/**
 * Safely decodes a base64 string, handling URL-safe variants and corrupted data.
 * Returns undefined if decoding fails (e.g., invalid characters from URL transit).
 *
 * Handles both standard base64 (old format) and URL-safe base64 without padding (new format).
 */
export function safeDecodeBase64(encoded: string): string | undefined {
  try {
    // Convert URL-safe base64 characters back to standard base64.
    // This replacement is safe because '-' and '_' are NOT valid characters in standard base64
    // (RFC 4648 Section 4: standard base64 uses A-Z, a-z, 0-9, +, /). They only appear in
    // URL-safe base64 (RFC 4648 Section 5) as substitutes for '+' and '/'. Therefore:
    // - If the input is standard base64: no replacement occurs (no '-' or '_' present)
    // - If the input is URL-safe base64: it gets converted to standard base64
    // - If the input is corrupted: atob() will throw and we return undefined
    const standardBase64 = cleanLegacyEncoding(encoded).replaceAll('-', '+').replaceAll('_', '/')

    // Restore padding that may have been stripped during encoding.
    // Standard base64 requires length to be a multiple of 4, padded with '='.
    // For already-padded input, (4 - (len % 4)) % 4 equals 0 — this is a no-op.
    const padded = standardBase64 + '='.repeat((4 - (standardBase64.length % 4)) % 4)

    return atob(padded)
  } catch {
    return undefined
  }
}

/**
 * Workaround the consequences of a historical bug which persisted bad values in localStorage
 */
function cleanLegacyEncoding(dirtyEncoding: string): string {
  const cleanEncoding = dirtyEncoding.endsWith('=%3D') ? dirtyEncoding.slice(0, -4) : dirtyEncoding
  return cleanEncoding.replaceAll('%2F', '/').replaceAll('%2B', '+')
}
